import { Link, useParams } from 'react-router-dom';
import {
  GraduationCap,
  CalendarCheck,
  Briefcase,
  BarChart3,
  Layers,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react';

interface ReportLink {
  label: string;
  description: string;
  to: string;
}

export interface ReportCategory {
  /** URL-safe key — the sidebar links to `/org/reports/category/:slug`, and this page filters to just the matching category. */
  slug: string;
  title: string;
  icon: LucideIcon;
  accent: string;
  iconTone: string;
  items: ReportLink[];
}

/**
 * Reports hub — groups every org-tier report into categories instead of
 * one long flat sidebar list, mirroring the real `state-reports.
 * dashboard` page's own 4-category card-grid layout (Student/Attendance/
 * Teacher/General Reports), plus a 5th "Operations & Data" category for
 * the extra org-tier screens (data export, affidavits, ID cards) that
 * live in this sidebar section but aren't part of the real 19.
 * Departments/Lessons/Lesson Topics live in the Academic sidebar group
 * instead (real legacy nests them there, not under Reports); Program
 * Dashboard lives in the Program Management sidebar group; Promotion
 * Report lives in the Schools & Admission sidebar group's Promotion
 * sub-menu (real legacy: `activeModule == 'schools_admission'`, not
 * Academic/Reports) — see OrgLayout.tsx for all three.
 */
export const CATEGORIES: ReportCategory[] = [
  {
    slug: 'student',
    title: 'Student Reports',
    icon: GraduationCap,
    accent: 'border-t-indigo-500',
    iconTone: 'bg-indigo-50 text-indigo-600',
    items: [
      { label: 'Student Summary Report', description: 'Per-school x per-class active student counts.', to: '/org/reports/student-summary' },
      { label: 'Online Admission', description: 'Review and manage online admission applicants.', to: '/org/online-admission/applicants' },
      { label: 'Admission Summary', description: 'Aggregated summary of all online admissions.', to: '/org/online-admission/summary' },
      { label: 'Students Report', description: 'Comprehensive list of all registered students.', to: '/org/reports/students' },
      { label: 'Student Portfolio', description: 'Search and view a detailed student portfolio.', to: '/org/reports/students/portfolio' },
      { label: 'Noorani Report', description: 'Quran-course registration leads.', to: '/org/reports/course-registrations' },
    ],
  },
  {
    slug: 'attendance',
    title: 'Attendance Reports',
    icon: CalendarCheck,
    accent: 'border-t-cyan-500',
    iconTone: 'bg-cyan-50 text-cyan-600',
    items: [
      { label: 'Student Attendance Report', description: 'Working-days/present/absent rollup per student.', to: '/org/reports/student-attendance' },
      { label: 'Detailed Student Attendance', description: 'Day-by-day present/absent matrix per student.', to: '/org/reports/detailed-student-attendance' },
      { label: 'Students Attendance Summary', description: 'Aggregated view of student attendance by school.', to: '/org/reports/students-attendance-summary' },
      { label: 'Teacher Attendance Report', description: 'Working-days/present/absent rollup per teacher.', to: '/org/reports/teacher-attendance' },
      { label: 'Detailed Teacher Attendance', description: 'Day-by-day present/absent matrix per teacher.', to: '/org/reports/detailed-teacher-attendance' },
      { label: 'Teachers Attendance Summary', description: 'Aggregated school-level view of teacher attendance.', to: '/org/reports/teachers-attendance-summary' },
      { label: 'Parent Attendance Summary', description: 'Aggregated school-level view of parent attendance.', to: '/org/guardian-attendance/summary' },
      { label: 'Parent Attendance (Center-wise)', description: 'Center-wise aggregated parent attendance.', to: '/org/reports/parent-attendance-center-wise' },
    ],
  },
  {
    slug: 'teacher',
    title: 'Teacher Reports',
    icon: Briefcase,
    accent: 'border-t-emerald-500',
    iconTone: 'bg-emerald-50 text-emerald-600',
    items: [
      { label: 'Teachers Report', description: 'Overview of all registered teachers across schools.', to: '/org/reports/teachers' },
      { label: 'Subject-wise Teacher Count', description: 'School-wise count of teachers per subject.', to: '/org/reports/subject-teacher-count' },
    ],
  },
  {
    slug: 'general',
    title: 'General Reports',
    icon: BarChart3,
    accent: 'border-t-amber-500',
    iconTone: 'bg-amber-50 text-amber-600',
    items: [
      { label: 'Finance Report', description: 'Detailed breakdown of financial transactions and fees.', to: '/org/reports/finance' },
      { label: 'Login Report', description: 'Track user logins and platform engagement.', to: '/org/reports/login' },
      { label: 'Video View Report', description: 'Analytics on instructional video views by users.', to: '/org/reports/video-view' },
      { label: 'MIS Reports', description: 'Cross-school management information dashboards.', to: '/org/mis-reports' },
      { label: 'Usage History', description: 'API usage logs across your organization.', to: '/org/reports/usage-logs' },
    ],
  },
  {
    slug: 'operations',
    title: 'Operations & Data',
    icon: Layers,
    accent: 'border-t-violet-500',
    iconTone: 'bg-violet-50 text-violet-600',
    items: [
      { label: 'Data Export', description: 'Bulk export of students and staff rosters.', to: '/org/reports/export' },
      { label: 'Affidavits', description: 'Student/guardian affidavit records.', to: '/org/reports/affidavits' },
      { label: 'Generate ID Cards', description: 'Bulk-generate student/staff ID cards.', to: '/org/id-cards/generate' },
    ],
  },
];

/**
 * One category at a time — the sidebar (OrgLayout.tsx) now lists just the
 * 5 category names directly; picking one routes here with `:slug` and
 * only that category's cards render on the right. Falls back to the
 * first category for an unknown/missing slug rather than a blank page.
 */
export function OrgReportsDashboardPage() {
  const { slug } = useParams<{ slug?: string }>();
  const category = CATEGORIES.find((c) => c.slug === slug) ?? CATEGORIES[0];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">{category.title}</h1>
        <p className="mt-0.5 text-sm text-slate-500">Organization-wide reports in this category.</p>
      </div>

      <div className={`card border-t-4 p-5 ${category.accent}`}>
        <div className="mb-4 flex items-center gap-2">
          <category.icon className="h-5 w-5 text-slate-700" />
          <h2 className="text-base font-semibold text-slate-900">{category.title}</h2>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {category.items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              state={{ orgNavGroup: 'Reports' }}
              className="group flex items-start gap-3 rounded-lg border border-slate-200 p-3 transition hover:border-slate-300 hover:bg-slate-50 hover:shadow-sm"
            >
              <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${category.iconTone}`}>
                <ChevronRight className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 group-hover:text-slate-900">{item.label}</div>
                <p className="mt-0.5 text-xs text-slate-500">{item.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
